import { useAuth0 } from "@auth0/auth0-react";
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { useEffect } from "react";

import { Logger } from "@moment/logging";
import { env } from "@moment/state";

const logger = new Logger("RustAuthListener");

/**
 * Listens for `auth:request-token` events from the Rust side and responds
 * with a fresh token via `provide_auth_token`. This enables on-demand auth
 * token resolution — Rust only requests a token when it encounters
 * `{{ env._moment.auth_token }}` in a request.
 *
 * Must be mounted inside `Auth0Provider` so `useAuth0()` is available.
 * Only active on desktop (Tauri) builds.
 */
export const RustAuthListener = () => {
	const { getAccessTokenSilently } = useAuth0();

	useEffect(() => {
		if (!env.isDesktopApp) return;

		const unlisten = listen<{ request_id: string }>("auth:request-token", async (event) => {
			const { request_id } = event.payload;
			logger.debug("Received auth:request-token", { request_id });

			let token: string | null = null;

			// Always attempt to get the token — getAccessTokenSilently will
			// return a cached token or refresh it if needed. Gating on
			// isAuthenticated would miss the window during Auth0 initialization
			// where a valid cached token exists but isAuthenticated is still false.
			try {
				const result = await getAccessTokenSilently();
				token = result ?? null;
			} catch {
				// Not authenticated or token unavailable — send null
			}

			try {
				await invoke("provide_auth_token", { requestId: request_id, token });
			} catch (err) {
				logger.error("Failed to provide auth token to Rust", { err });
			}
		});

		return () => {
			void unlisten.then((fn_) => fn_());
		};
	}, [getAccessTokenSilently]);

	return null;
};
